// lib/utils/getResolvedPageComponents.ts
import { IPersonalizationTriggers } from "../types/global";
import { resolvePersonalizedComponent } from "./resolve-personalized-components";
import { logPageError, logPageRequestDebug } from "../helper/logger";

const ADAPTER_NAME = "getResolvedPageComponents";

export function getResolvedPageComponents(
  pageComponents: any[],
  personalizedComponents: any[],
  triggers: IPersonalizationTriggers,
  page: string,
  personalizationMapping?: any,
  pageSessionId?: string
) {
  try {
    logPageRequestDebug(ADAPTER_NAME, `Resolving Page Components for ${  page || ""}`, {
      pageComponentsLength: pageComponents?.length || 0,
      personalizedComponentsLength: personalizedComponents?.length || 0,
      pageSessionId
    });
    const cookieList = [];
    const matchedKeys = new Set<string>();

    const updatedPageComponents = pageComponents.map(component => {
      const key = component?.personalizationKey;
      if (!key) {
        return component;
      }
      matchedKeys.add(key);
      const matchingPersonalized = personalizedComponents?.find(
        p => p.key === key
      );
      if (!matchingPersonalized) {
        return component;
      }
      const personalizedData = resolvePersonalizedComponent(
        matchingPersonalized,
        triggers,
        page,
        personalizationMapping,
        pageSessionId
      );

      if (personalizedData?.cookieList?.length > 0) {
        cookieList.push(...personalizedData.cookieList);
      }

      if (!personalizedData?.component) {
        return component;
      }

      // When a personalizedContent block has no default variant in CMS,
      // page-adapter pushes a typeless placeholder so the swap can land at
      // its declared position. In that case the swap is effectively an
      // inject — stamp pznInjected/pznKey so modal variants can opt into
      // immediate display via ModalOrchestrator. Non-modal components
      // ignore the flags and just render at the correct position.
      const originalIsPlaceholder = !component?.type && !component?.props?.type;
      if (originalIsPlaceholder) {
        return {
          ...personalizedData.component,
          props: {
            ...(personalizedData.component.props || {}),
            pznInjected: true,
            pznKey: key
          }
        };
      }
      return personalizedData?.component;
    });

    // Inject mode: personalized components with no matching page component
    // (no default variant → nothing to swap, inject if trigger matches)
    personalizedComponents?.forEach(p => {
      if (!p.key || matchedKeys.has(p.key)) {
        return;
      }

      const personalizedData = resolvePersonalizedComponent(
        p,
        triggers,
        page,
        personalizationMapping,
        pageSessionId
      );

      if (personalizedData?.cookieList?.length > 0) {
        cookieList.push(...personalizedData.cookieList);
      }

      if (personalizedData?.component) {
        updatedPageComponents.push({
          ...personalizedData.component,
          props: {
            ...(personalizedData.component.props || {}),
            pznInjected: true,
            pznKey: p.key
          }
        });
      }
    });

    return {
      components: updatedPageComponents,
      cookieList
    };
  } catch (error) {
    logPageError(ADAPTER_NAME, error, {
      pageComponentsLength: pageComponents?.length || 0,
      personalizedComponentsLength: personalizedComponents?.length || 0,
      pageSessionId
    });
    return {
      components: pageComponents,
      cookieList: []
    };
  }
}

export default getResolvedPageComponents;
